import { BasePageType } from "../base";
import ProfilePage from "./ProfilePage";

// Sample reading stats data
const sampleReadingStats = {
	booksRead: 42,
	pagesRead: 12568,
	averageRating: 4.2,
	readingStreak: 15,
	yearlyGoal: 52,
	yearlyProgress: 42,
	favoriteGenres: ["Fiction", "Science Fiction", "Mystery"],
	readingTime: 156, // hours
	completionRate: 85, // percentage
};

// Sample reading history
const sampleReadingHistory = [
	{
		id: 1,
		title: "The Midnight Library",
		author: "Matt Haig",
		dateCompleted: "2024-03-01",
		rating: 5,
		review: "A thought-provoking exploration of life's infinite possibilities.",
		coverImage: "https://picsum.photos/200/300",
	},
	{
		id: 2,
		title: "Project Hail Mary",
		author: "Andy Weir",
		dateCompleted: "2024-02-15",
		rating: 4,
		review: "Brilliant sci-fi with a perfect blend of science and storytelling.",
		coverImage: "https://picsum.photos/200/300?random=2",
	},
	{
		id: 3,
		title: "Tomorrow, and Tomorrow, and Tomorrow",
		author: "Gabrielle Zevin",
		dateCompleted: "2024-01-30",
		rating: 5,
		review: "A beautiful story about friendship, creativity, and gaming.",
		coverImage: "https://picsum.photos/200/300?random=3",
	},
];

// Sample event history
const sampleEventHistory = [
	{
		id: 1,
		title: "Monthly Book Club Meeting: 'The Midnight Library'",
		date: "2024-03-01",
		role: "Participant",
		contribution: "Led discussion on parallel universes theme",
	},
	{
		id: 2,
		title: "Author Meet & Greet: Mystery Writing Workshop",
		date: "2024-02-15",
		role: "Attendee",
		contribution: "Asked questions during Q&A",
	},
	{
		id: 3,
		title: "Children's Reading Circle",
		date: "2024-01-30",
		role: "Volunteer",
		contribution: "Helped organize activities",
	},
];

// Sample account settings
const sampleAccountSettings = {
	notifications: {
		bookUpdates: true,
		eventReminders: true,
		communityMessages: true,
		readingGoals: true,
	},
	privacy: {
		showReadingProgress: true,
		showReviews: true,
		showAttendance: true,
		profileVisibility: "public",
	},
	subscription: {
		plan: "Premium",
		renewalDate: "2024-12-31",
		features: ["Unlimited Reading", "Event Priority", "Ad-free Experience"],
	},
};

// Sample profile data with dynamic tabs
const sampleProfile = {
	name: "Sarah Chen",
	avatar: "https://i.pravatar.cc/150?img=64",
	bio: "Avid reader and book club enthusiast. Always looking for the next great story.",
	location: "San Francisco, CA",
	memberSince: "2023-01-15",
	badges: ["Top Reader", "Discussion Leader", "Event Organizer"],
	socialLinks: {
		twitter: "@sarahreads",
		instagram: "@sarahsbookshelf",
		goodreads: "sarahchen",
	},
	// Dynamic tabs configuration
	tabsConfig: [
		{
			id: "tab1",
			label: "Tab 1",
			icon: "grid-outline",
			showIcon: true,
			showLabel: true,
			backgroundColor: "#ffffff",
			font: {
				family: "System",
				size: 14,
				weight: "400",
				color: "#2a2a2a",
			},
			content: {
				sections: [],
				layout: "default",
			},
		},
		{
			id: "tab2",
			label: "Tab 2",
			icon: "grid-outline",
			showIcon: true,
			showLabel: true,
			backgroundColor: "#ffffff",
			font: {
				family: "System",
				size: 14,
				weight: "400",
				color: "#2a2a2a",
			},
			content: {
				sections: [],
				layout: "default",
			},
		},
		{
			id: "tab3",
			label: "Tab 3",
			icon: "grid-outline",
			showIcon: true,
			showLabel: true,
			backgroundColor: "#ffffff",
			font: {
				family: "System",
				size: 14,
				weight: "400",
				color: "#2a2a2a",
			},
			content: {
				sections: [],
				layout: "default",
			},
		},
	],
};

export const ProfilePageType = {
	...BasePageType,
	component: ProfilePage,
	icon: "person-outline",
	title: "Profile",
	getDefaultContent: () => ({
		profile: sampleProfile,
	}),
	// Section types that can be added to any tab
	sectionTypes: {
		...BasePageType.sectionTypes,
		canvas: {
			type: "canvas",
			getDefault: () => ({
				id: `canvas-${Date.now()}`,
				type: "canvas",
				name: "Scrollable Canvas",
				isVisible: true,
				layout: "default",
				content: "posts",
				gem: [],
			}),
		},
	},
	// Tab specific methods
	tabs: {
		addTab: (profile, tabConfig) => {
			const newTab = {
				id: `tab-${Date.now()}`,
				label: "New Tab",
				icon: "grid-outline",
				showIcon: true,
				showLabel: true,
				backgroundColor: "#ffffff",
				font: {
					family: "System",
					size: 14,
					weight: "400",
					color: "#2a2a2a",
				},
				content: {
					sections: [],
					layout: "default",
				},
				...tabConfig,
			};

			profile.tabsConfig = [...(profile.tabsConfig || []), newTab];
			return newTab.id;
		},
		removeTab: (profile, tabId) => {
			profile.tabsConfig = (profile.tabsConfig || []).filter((tab) => tab.id !== tabId);
		},
		updateTabContent: (profile, tabId, content) => {
			const tab = (profile.tabsConfig || []).find((tab) => tab.id === tabId);
			if (tab) {
				tab.content = { ...tab.content, ...content };
			}
		},
	},
};

export default ProfilePageType;
